import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Lock, Flame, RotateCcw, Loader2, CalendarDays, Flag } from 'lucide-react';
import { progressAPI, challengesAPI } from '../services/api';
import { ValidateDialog } from '../components/ValidateDialog';

const STATUS_STYLE = {
  valide: 'bg-green-600 text-white border-green-600',
  rattrape: 'bg-cyan-500 text-white border-cyan-500',
  a_faire: 'bg-white dark:bg-red-950 text-orange-600 border-orange-400 ring-2 ring-orange-300 animate-pulse',
  manque: 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700',
  upcoming: 'bg-muted text-muted-foreground/50 border-transparent',
};

export default function Journey() {
  const [data, setData] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    progressAPI.me().then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    challengesAPI.list().then(setChallenges).catch(() => {});
  }, [load]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;

  const openDay = (day) => {
    if (day.status === 'upcoming') return;
    const full = challenges.find((c) => c.day_number === day.day_number);
    if (full) { setSelected(full); setOpen(true); }
  };

  // group by secret
  const groups = {};
  data.calendar.forEach((d) => {
    groups[d.secret_number] = groups[d.secret_number] || { secret: d.secret, days: [] };
    groups[d.secret_number].days.push(d);
  });

  const validatedCount = data.calendar.filter((d) => d.status === 'valide' || d.status === 'rattrape').length;
  const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="space-y-6 animate-rise">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">🔥 Ton parcours 21 jours</h1>
        <p className="text-muted-foreground text-sm mt-1">Tu es au jour {data.current_day}. Clique sur un jour pour valider ou rattraper.</p>
        {data.start_date && (
          <div data-testid="challenge-dates" className="flex flex-wrap items-center gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold border border-orange-500/20">
              <CalendarDays className="w-3.5 h-3.5" />Démarrage : {fmtDate(data.start_date)}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-semibold border border-green-500/20">
              <Flag className="w-3.5 h-3.5" />Fin : {fmtDate(data.end_date)}
            </span>
          </div>
        )}
      </div>

      {/* Encouragement banner */}
      <div data-testid="journey-encouragement" className="rounded-2xl p-4 bg-gradient-to-r from-orange-50 via-green-50 to-red-50 dark:from-orange-950/30 dark:via-green-950/20 dark:to-red-950/30 border border-green-100 dark:border-green-900/40 flex items-center gap-3">
        <span className="text-3xl" aria-hidden>{validatedCount >= 10 ? '💪' : validatedCount >= 1 ? '🌱' : '✨'}</span>
        <p className="text-sm font-semibold text-red-950 dark:text-red-50">
          {validatedCount === 0
            ? "C'est parti ! Chaque petit pas compte. 🚀"
            : validatedCount >= 21
            ? 'Bravo championne, parcours terminé ! 🏆'
            : `${validatedCount} jour${validatedCount > 1 ? 's' : ''} validé${validatedCount > 1 ? 's' : ''} — continue comme ça ! 💚`}
        </p>
      </div>

      {Object.entries(groups).map(([sn, g]) => (
        <Card key={sn} className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bfc-gradient flex items-center justify-center text-white font-extrabold text-sm">{sn}</div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-red-700/80 dark:text-red-400">Secret {sn}</p>
              <p className="font-bold text-red-950 dark:text-red-50 text-sm leading-tight">{g.secret}</p>
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2.5">
            {g.days.map((d) => (
              <button
                key={d.day_number}
                onClick={() => openDay(d)}
                disabled={d.status === 'upcoming'}
                data-testid={`day-cell-${d.day_number}`}
                className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-0.5 transition-all hover:scale-105 disabled:hover:scale-100 disabled:cursor-not-allowed ${STATUS_STYLE[d.status]}`}
              >
                {d.status === 'valide' || d.status === 'rattrape' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : d.status === 'a_faire' ? (
                  <Flame className="w-4 h-4" />
                ) : d.status === 'manque' ? (
                  <RotateCcw className="w-4 h-4" />
                ) : (
                  <Lock className="w-3.5 h-3.5" />
                )}
                <span className="text-sm font-extrabold">{d.day_number}</span>
              </button>
            ))}
          </div>
        </Card>
      ))}

      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-600" />Validé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-cyan-500" />Rattrapé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-orange-400" />Aujourd'hui</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-300" />Manqué (rattrapable)</span>
        <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" />À venir</span>
      </div>

      <ValidateDialog open={open} onOpenChange={setOpen} challenge={selected} onValidated={load} />
    </div>
  );
}
